class PremiumValues extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-reveal-ready');
    this.visual = this.querySelector('[data-pv-visual]');
    this.stage = this.querySelector('[data-pv-stage]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);
    this.visual?.addEventListener('pointermove', this.onVisualPointerMove);
    this.visual?.addEventListener('pointerleave', this.resetVisualTilt);
    this.setupReveal();
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeyDown);
    this.visual?.removeEventListener('pointermove', this.onVisualPointerMove);
    this.visual?.removeEventListener('pointerleave', this.resetVisualTilt);
    this.observer?.disconnect();
    cancelAnimationFrame(this.tiltFrame);
    clearTimeout(this.visualTimer);
  }

  onPointerDown = (event) => {
    const card = event.target.closest('[data-pv-card]');
    if (card && this.contains(card)) {
      this.createRipple(card.querySelector('[data-pv-ripple]'), card, event.clientX, event.clientY);
      return;
    }

    const visual = event.target.closest('[data-pv-visual]');
    if (visual && this.contains(visual)) {
      this.createRipple(visual.querySelector('[data-pv-image-ripple]'), visual, event.clientX, event.clientY);
    }
  };

  onClick = (event) => {
    const card = event.target.closest('[data-pv-card]');
    if (card && this.contains(card) && card.tagName === 'BUTTON') {
      const isSelected = !card.classList.contains('is-selected');
      this.querySelectorAll('[data-pv-card].is-selected').forEach((item) => {
        item.classList.remove('is-selected');
        if (item.tagName === 'BUTTON') item.setAttribute('aria-pressed', 'false');
      });
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', String(isSelected));
      return;
    }

    const visual = event.target.closest('[data-pv-visual]');
    if (visual && this.contains(visual)) this.activateVisual();
  };

  onKeyDown = (event) => {
    if (event.target !== this.visual || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    this.activateVisual();
  };

  activateVisual() {
    this.visual?.classList.remove('is-activated');
    requestAnimationFrame(() => this.visual?.classList.add('is-activated'));
    clearTimeout(this.visualTimer);
    this.visualTimer = setTimeout(() => this.visual?.classList.remove('is-activated'), 900);
  }

  createRipple(ripple, container, clientX, clientY) {
    if (!ripple || !container || this.reducedMotion.matches) return;
    const rect = container.getBoundingClientRect();
    ripple.style.left = `${clientX - rect.left}px`;
    ripple.style.top = `${clientY - rect.top}px`;
    ripple.classList.remove('is-active');
    requestAnimationFrame(() => ripple.classList.add('is-active'));
    ripple.addEventListener('animationend', () => ripple.classList.remove('is-active'), { once: true });
  }

  onVisualPointerMove = (event) => {
    if (
      this.dataset.enableImageTilt !== 'true' ||
      this.reducedMotion.matches ||
      !this.finePointer.matches ||
      !this.stage
    ) return;

    const rect = this.visual.getBoundingClientRect();
    const horizontal = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    const vertical = ((event.clientY - rect.top) / rect.height - 0.5) * 2;

    cancelAnimationFrame(this.tiltFrame);
    this.tiltFrame = requestAnimationFrame(() => {
      this.stage.style.setProperty('--pv-tilt-x', `${horizontal * 2.2}deg`);
      this.stage.style.setProperty('--pv-tilt-y', `${vertical * -2.2}deg`);
    });
  };

  resetVisualTilt = () => {
    if (!this.stage) return;
    this.stage.style.setProperty('--pv-tilt-x', '0deg');
    this.stage.style.setProperty('--pv-tilt-y', '0deg');
  };

  setupReveal() {
    if (!('IntersectionObserver' in window)) {
      this.classList.add('is-revealed', 'is-in-view');
      return;
    }

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.classList.toggle('is-in-view', entry.isIntersecting);
        if (entry.isIntersecting) this.classList.add('is-revealed');
      },
      { rootMargin: '120px 0px', threshold: 0.06 }
    );
    this.observer.observe(this);
  }
}

if (!customElements.get('premium-values')) {
  customElements.define('premium-values', PremiumValues);
}
